import { action, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import Workspace from 'iam/models/Workspace'
import { CancellationToken } from 'infra/cancellation'
import ModalViewModel from 'infra/ModalViewModel'
import { CursorPageQuery, Id, Nullable } from 'types/definitions'
import { JoinWorkspaceAsAdminPort } from './ports'

export class JoinWorkspaceAsAdminViewModel extends ModalViewModel {
  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * Validation rules for the join workspace form.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
  })

  constructor(
    private _getUserMembershipWorkspaceIds: () => string[],
    private _refreshMemberships: () => Promise<void>,
    private newAlert: AlertFactory,
    private port: JoinWorkspaceAsAdminPort,
  ) {
    super()
    makeObservable(this)
    this.searchWorkspaces = this.searchWorkspaces.bind(this)
  }

  /**
   * Searches for workspaces.
   */
  searchWorkspaces(
    searchText: string,
    pagination: CursorPageQuery,
    _ct?: CancellationToken,
  ) {
    return this.port.listAvailableWorkspaces({
      searchText,
      excludeWorkspaceIds: this._getUserMembershipWorkspaceIds(),
      pagination,
    })
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
  }

  /**
   * Refreshes the user's workspace memberships.
   */
  async refreshMemberships() {
    await this._refreshMemberships()
  }

  /**
   * Join the selected workspace as admin.
   */
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const alert = this.newAlert()

    if (!this.workspace) {
      alert.fromError('Please select a workspace').autoDismiss().show()
      return
    }

    const workspaceName = this.workspace.name

    try {
      await this.port.joinWorkspaceAsAdmin(Id.assert(this.workspace.id))
    } catch (error) {
      alert.fromError('Error joining workspace as admin').autoDismiss().show()
      console.error('Error joining workspace as admin:', error)
      return
    }

    await this.refreshMemberships()

    alert
      .withState(AlertState.Success)
      .saying('Success!', `Joined ${workspaceName} as admin.`)
      .autoDismiss()
      .show()

    this.close()
  })

  @action.bound
  reset() {
    this.setWorkspace(null)
    this.validation.reset()
  }

  @override
  show() {
    super.show()
  }

  @override
  close() {
    super.close()
    this.reset()
  }
}
